import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TalentService } from '../talent.service';
import { TestAssignmentService } from '../test-assignment.service';

@Component({
    selector: 'app-test-assignment-list',
    templateUrl: './test-assignment-list.component.html',
    styleUrls: ['./test-assignment-list.component.css'],
    standalone: false
})
export class TestAssignmentListComponent implements OnInit {
  assignments: any = [];
  filteredAssignments: any = [];
  
  // Filters
  selectedStatus: string = '';
  searchText: string = '';
  days: number = 30;
  
  // Sorting
  sortField: string = 'date_assigned';
  sortReverse: boolean = true;
  
  isLoading: boolean = false;
  errorMessage: string = '';

  constructor(
    private talentService: TalentService,
    private testAssignmentService: TestAssignmentService,
    private router: Router
  ) {}

  ngOnInit() {
    this.loadAssignments();
  }

  loadAssignments() {
    this.isLoading = true;
    this.errorMessage = '';

    this.talentService.getAllTestAssignments(this.days).subscribe(
      (assignments) => {
        this.assignments = assignments;
        this.applyFilters();
        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading assignments:', error);
        this.errorMessage = 'Failed to load test assignments';
        this.isLoading = false;
      }
    );
  }

  applyFilters() {
    this.filteredAssignments = this.assignments.filter(assignment => {
      if (this.selectedStatus && assignment.status !== this.selectedStatus) {
        return false;
      }
      if (this.searchText) {
        const searchLower = this.searchText.toLowerCase();
        const matchesSearch = 
          (assignment.candidate_name && assignment.candidate_name.toLowerCase().includes(searchLower)) ||
          (assignment.candidate_email && assignment.candidate_email.toLowerCase().includes(searchLower)) ||
          (assignment.test_name && assignment.test_name.toLowerCase().includes(searchLower)) ||
          (assignment.id && assignment.id.toString().includes(searchLower));
        if (!matchesSearch) return false;
      }
      return true;
    });

    // Apply sorting
    this.filteredAssignments.sort((a, b) => {
      let aVal = a[this.sortField];
      let bVal = b[this.sortField];
      
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (this.sortReverse) {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      } else {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      }
    });
  }

  setSortField(field: string) {
    if (this.sortField === field) {
      this.sortReverse = !this.sortReverse;
    } else {
      this.sortField = field;
      this.sortReverse = false;
    }
    this.applyFilters();
  }

  clearFilters() {
    this.selectedStatus = '';
    this.searchText = '';
    this.days = 30;
    this.loadAssignments();
  }

  viewAssignment(assignmentId: number) {
    this.router.navigate(['/test-assignment-detail', assignmentId]);
  }

  viewTest(testId: number) {
    this.router.navigate(['/ai-test-detail', testId]);
  }

  viewCandidate(candidateId: number) {
    this.router.navigate(['/candidate-detail', candidateId]);
  }

  getTestLink(code: string): string {
    return this.testAssignmentService.getTestLink(code);
  }

  copyTestLink(code: string): void {
    this.testAssignmentService.copyTestLink(code);
  }

  getStatusBadgeClass(status: string): string {
    return this.testAssignmentService.getStatusBadgeClass(status);
  }

  getStatusDisplay(status: string): string {
    return this.testAssignmentService.getStatusDisplay(status);
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  }

  getDifficultyDisplay(difficulty: string): string {
    if (!difficulty) return 'N/A';
    return difficulty.charAt(0).toUpperCase() + difficulty.slice(1);
  }

  getDifficultyBadgeClass(difficulty: string): string {
    switch(difficulty) {
      case 'basic': return 'badge-success';
      case 'intermediate': return 'badge-warning';
      case 'advanced': return 'badge-danger';
      default: return 'badge-secondary';
    }
  }

  navigateToTestList() {
    this.router.navigate(['/ai-test-list']);
  }

  cancelAssignment(event: Event, assignment: any) {
    event.stopPropagation();

    if (assignment.status === 'completed') {
      alert('Cannot cancel a completed assessment');
      return;
    }

    if (assignment.status === 'cancelled') {
      alert('This assessment is already cancelled');
      return;
    }

    if (!confirm(`Are you sure you want to cancel the assessment for ${assignment.candidate_name}? They will no longer be able to access it.`)) {
      return;
    }

    this.talentService.cancelTestAssignment(assignment.id).subscribe(
      (response) => {
        if (response.status === 'success') {
          // Update the assignment status in the list
          assignment.status = 'cancelled';
          this.applyFilters();
        } else {
          alert(response.message || 'Failed to cancel assessment');
        }
      },
      (error) => {
        console.error('Error cancelling assessment:', error);
        alert('Failed to cancel assessment');
      }
    );
  }
}
